<template>
  <div class="d-flex align-items-center justify-content-end mb-md-4 mt-3 mt-md-0">
    <span v-if="selectable" class="font-weight-light">{{ $t("vybrano") }}: {{ selectedRows.length }}</span>
    <span v-if="selectedRows.length > 0" class="link ml-2" @click="selectAllOnPage('deselect')">
      {{ $t("OdvybratVse") }}
    </span>
  </div>
  <div class="position-relative w-100">
    <div v-if="items.length" :id="tableId" class="table-responsive">
      <table class="table-fixed table table-main table-hover table-sticky" :style="{ minWidth: tableMinWidth }">
        <thead>
          <tr>
            <th
              v-for="(column, index) in columns"
              :key="index"
              :class="[
                `col-${column.name}`,
                { isHidden: column.hidden || column.visible === false, 'sticky-col-first': index === 0 }
              ]"
              :style="{ width: column.minWidth || '150px' }"
            >
              <span class="th-inner d-flex align-items-center">
                <div
                  v-if="index === 0 && selectable"
                  :class="{ checked: isSelectedAllOnPage }"
                  class="custom-control custom-control--checkbox"
                  @click="selectAllOnPage(isSelectedAllOnPage ? 'deselect' : 'selectAll')"
                >
                  <input class="custom-control-input" type="checkbox" />
                </div>
                <template v-if="column.sortable != true">{{ $t(column.title ?? column.name) }}</template>
                <template v-else>
                  <div class="title" @click="sortItemsBy(column.sortBy ?? column.name)">
                    {{ $t(column.title ?? column.name) }}
                  </div>
                  <div v-if="sortBy == (column.sortBy ?? column.name)" class="sorting">
                    <span v-if="sortType == 'desc'">▼</span>
                    <span v-if="sortType == 'asc'">▲</span>
                  </div>
                </template>
              </span>
            </th>
            <th v-if="hasActions" class="sticky-col-last" :style="{ width: actionsWidth }" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, item_index) in pageItems" :key="item_index" :class="{ 'grey-bg': item.suspended }">
            <td
              v-for="(column, column_index) in columns"
              :key="column_index"
              :class="[
                `col-${column.name}`,
                {
                  isHidden: column.hidden || column.visible === false,
                  'grey-bg': item.suspended,
                  'sticky-col-first': column_index === 0
                }
              ]"
              :style="{ width: column.minWidth || '150px' }"
            >
              <div class="d-flex">
                <div
                  v-if="column_index === 0 && (selectable || singleSelectable) && item.projectId === selectedProjectId"
                  :class="{ checked: selectedRows.filter((e) => e[itemId] === item[itemId]).length > 0 }"
                  class="custom-control custom-control--checkbox"
                  style="display: inline-block"
                  @click="addToSelected(item)"
                >
                  <input class="custom-control-input" type="checkbox" />
                </div>
                <span
                  v-if="column_index === 0"
                  style="display: block; overflow-wrap: anywhere; min-width: 0; width: 100%"
                >
                  <slot :item="item" :name="column.name">
                    {{ item[column.name] }}
                  </slot>
                </span>
                <slot v-else :item="item" :name="column.name">
                  {{ item[column.name] }}
                </slot>
              </div>
            </td>
            <td v-if="hasActions" class="sticky-col-last">
              <slot :item="item" name="actions" />
              <div v-if="kebab" :class="{ opened: openedItem[itemId] === item[itemId] }" class="kebab">
                <div class="dropdown-actions">
                  <div class="atom-icon d-block mr-0" @click="openKebab(item, $event)">
                    <img src="@/assets/icons/menu/ico-kebab.svg" />
                  </div>
                  <Teleport to="body">
                    <div
                      v-if="openedItem[itemId] === item[itemId]"
                      class="dropdown-actions__wrapper dropdown-actions__wrapper--fixed"
                      :style="kebabStyle"
                    >
                      <div class="dropdown-actions__inner">
                        <slot :item="item" name="kebab-actions" />
                      </div>
                    </div>
                  </Teleport>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div v-if="items.length === 0 && emptyText">
    <div class="w-100">
      <p>{{ $t(emptyText) }}</p>
    </div>
  </div>

  <div
    v-if="sortedItems.length > 0"
    class="custom-table__footer w-100 d-flex align-items-center justify-content-between"
  >
    <div class="custom-table__footer__actions align-items-center">
      <slot name="customFooterActions" />
    </div>
    <Paging
      :page="{
        currentPage: page,
        rowsPerPage: rowsPerPage,
        itemsTotalCount: itemsTotalCount,
        itemsCount: sortedItems.length
      }"
      @page="page = 0"
      @next-page="page = page + 1"
      @prev-page="page = page - 1"
      @reload-items="$emit('reloadItems')"
      @rows-per-page="rowsPerPage = $event"
    />
  </div>
</template>

<script>
import _ from "lodash"

import Paging from "@/components/paging/paging.vue"

export default {
  components: { Paging },
  props: {
    columns: Array,
    items: Array,
    fullWidth: Boolean,
    loading: Boolean,
    emptyText: String,
    kebab: Boolean,
    itemId: String,
    selectable: Boolean,
    singleSelectable: Boolean,
    defaultSortingIndex: {
      type: Number,
      default: 0
    },
    defaultSortType: {
      type: String,
      default: "asc"
    },
    tableId: String,
    itemsTotalCount: Number,
    actionsWidth: {
      type: String,
      default: "50px"
    }
  },
  emits: ["reloadItems", "pageSizeChange", "pageChange", "rowsSelected", "opened", "sortBy"],
  data() {
    return {
      selectedProjectId: this.$route.params.projectId,
      sortType: "asc",
      sortBy: "",
      sortedItems: [],
      rowsPerPage: 20,
      page: 0,
      openedItem: {},
      openedRowsSelect: false,
      selectedRows: [],
      showKebab: false,
      kebabStyle: {},
      kebabTriggerEl: null
    }
  },
  computed: {
    pageItems() {
      if (this.sortedItems.length < this.itemsTotalCount) {
        return this.sortedItems
      }

      return this.sortedItems.slice(this.page * this.rowsPerPage, Number((this.page + 1) * this.rowsPerPage))
    },
    isSelectedAllOnPage() {
      const selectedIds = this.selectedRows.map((e) => e[this.itemId])
      return this.pageItems.every((e) => selectedIds.includes(e[this.itemId]))
    },
    hasActions() {
      return this.kebab || !!this.$slots.actions || !!this.$slots["kebab-actions"]
    },
    tableMinWidth() {
      const toPx = (w) => parseInt(String(w).replace("px", ""), 10) || 150
      const visible = this.columns.filter((c) => !(c.hidden || c.visible === false))
      let sum = visible.reduce((acc, c) => acc + (c.minWidth ? toPx(c.minWidth) : 150), 0)
      if (this.hasActions) sum += toPx(this.actionsWidth)
      return sum + "px"
    }
  },
  watch: {
    rowsPerPage() {
      this.page = 0
      this.$emit("pageSizeChange", this.rowsPerPage)
      localStorage.rowsPerPage = this.rowsPerPage
    },
    page() {
      this.$emit("pageChange", this.page)
    },
    items() {
      this.sortedItems = [].concat(this.items)
      // this.sortBy = this.columns[this.defaultSortingIndex].name
      // this.sortItemsBy(this.sortBy, true)
      this.$nextTick(() => {
        this.updateResponsiveTable()
      })
    },
    openedItem() {
      if (this.openedItem[this.itemId]) {
        this.$emit("opened", this.openedItem)
      } else {
        this.$emit("opened", null)
      }
    },
    selectedRows() {
      this.$emit("rowsSelected", this.selectedRows)
    }
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener("resize", this.updateResponsiveTable)
      window.addEventListener("click", this.updateResponsiveTable)
      window.addEventListener("scroll", this.closeKebab, true)
      window.addEventListener("resize", this.closeKebab)
    })

    this.sortType = this.defaultSortType

    this.selectedRows = []
    if (localStorage.rowsPerPage) {
      var rows1 = localStorage.rowsPerPage
      this.rowsPerPage = rows1
    } else {
      localStorage.rowsPerPage = this.rowsPerPage
    }
    this.sortedItems = [].concat(this.items)
    this.sortBy = this.columns[this.defaultSortingIndex].name

    if (this.defaultSortingIndex > 0) {
      var self = this
      setTimeout(function () {
        self.sortItemsBy(self.sortBy, true)
      }, 1)
    }
    this.$nextTick(() => {
      this.updateResponsiveTable()
    })
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateResponsiveTable)
    window.removeEventListener("click", this.updateResponsiveTable)
    window.removeEventListener("scroll", this.closeKebab, true)
    window.removeEventListener("resize", this.closeKebab)
    document.removeEventListener("click", this.closeKebabOnOutside)
  },
  methods: {
    getTableColumns() {
      const localStorageItemsJSON = localStorage.getItem(this.tableId + "TableColumns")
      if (!localStorageItemsJSON) {
        return this.columns
      }

      const localStorageItems = JSON.parse(localStorageItemsJSON)

      if (
        Object.keys(localStorageItems).length === 0 ||
        Object.keys(localStorageItems).length !== Object.keys(this.columns).length
      ) {
        return this.columns
      }
      return localStorageItems
    },
    updateResponsiveTable() {
      this.$updateResponsiveTable(this, this.tableId)
    },
    sortItemsBy(sortBy, skipSortType) {
      let sorted
      this.sortBy = sortBy

      // we need to skip sortType change after language change for example
      if (!skipSortType) {
        if (this.sortType == "asc") {
          this.sortType = "desc"
        } else {
          this.sortType = "asc"
        }
      }

      this.$emit("sortBy", { sortBy, sortType: this.sortType })
      if (this.items.length < this.itemsTotalCount) {
        return
      }

      sorted = _.orderBy(this.sortedItems, [(item) => item[sortBy]?.toLowerCase()], this.sortType)

      this.sortedItems = sorted
    },
    openKebab(item, event) {
      // Toggle: clicking the icon of the already-open row closes it (compare by
      // id so a list reload that swaps item objects doesn't break the toggle).
      if (this.openedItem[this.itemId] === item[this.itemId]) {
        this.closeKebab()
        return
      }
      this.showKebab = true
      this.openedItem = item
      this.kebabTriggerEl = event ? event.currentTarget : null
      if (event) {
        // The menu is teleported to <body> and position:fixed so it escapes the
        // .table-responsive scroll container (which would clip it) and its
        // stacking context (which would let other rows' icons paint over it).
        // Position it under the icon, right-aligned, clamped on-screen; measure
        // after render because the menu size is responsive (251px / 401px).
        const rect = event.currentTarget.getBoundingClientRect()
        this.kebabStyle = { top: rect.bottom + 8 + "px", left: Math.max(8, rect.right - 251) + "px" }
        this.$nextTick(() => {
          const menu = document.querySelector(".dropdown-actions__wrapper--fixed")
          const w = menu ? menu.offsetWidth : 251
          const h = menu ? menu.offsetHeight : 0
          const left = Math.max(8, Math.min(rect.right - w, window.innerWidth - w - 8))
          // Open below the icon, but flip above it if the menu would overflow
          // the viewport bottom.
          let top = rect.bottom + 8
          if (h && top + h > window.innerHeight - 8) {
            top = Math.max(8, rect.top - 8 - h)
          }
          this.kebabStyle = { top: top + "px", left: left + "px" }
          document.addEventListener("click", this.closeKebabOnOutside)
        })
      }
    },
    closeKebabOnOutside(event) {
      // Clicks on the trigger icon are handled by openKebab's toggle; ignore them
      // here so the menu doesn't close-then-reopen. Any other click closes it.
      if (this.kebabTriggerEl && this.kebabTriggerEl.contains(event.target)) {
        return
      }
      this.closeKebab()
    },
    addToSelected(item) {
      if (this.selectedRows.filter((e) => e[this.itemId] === item[this.itemId]).length > 0) {
        this.selectedRows = this.selectedRows.filter((e) => e[this.itemId] !== item[this.itemId])
      } else {
        if (this.singleSelectable) {
          this.selectedRows = []
        }
        this.selectedRows = [...this.selectedRows, item]
      }
    },
    selectAllOnPage(type) {
      if (type === "deselect") {
        const pageItemsIds = this.pageItems.map((e) => e[this.itemId])
        this.selectedRows = this.selectedRows.filter((e) => !pageItemsIds.includes(e[this.itemId]))
      } else if (type === "selectAll") {
        this.selectedRows = [...this.selectedRows, ...this.pageItems]
      }
    },
    openModal1(item) {
      this.$emit("openModal1", item)
    },
    closeKebab() {
      if (!this.showKebab) {
        return
      }
      this.showKebab = false
      this.openedItem = {}
      this.kebabTriggerEl = null
      document.removeEventListener("click", this.closeKebabOnOutside)
    }
  }
}
</script>

<style lang="scss" scoped></style>
